import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Observable } from 'rxjs';

import { FieldOption } from '../components/dynamic-form/interfaces/field-option.interface';
import { FormConfig } from '../components/dynamic-form/interfaces/form-config.interface';
import { FormState } from '../components/dynamic-form/types/form-state.type';
import { API_ROUTES } from '../consts/api-routes.const';
import { HttpService } from './http.service';

export interface FormFieldValue {
  id: string;
  value: unknown;
}

/**
 * Body shape for POST /wp/planner/jobs: the raw FormState (field-id → value)
 * keyed exactly like the form definition's `state`. This is what the backend's
 * DynamicFormValidator validates — NOT the per-option-boolean submission array.
 *
 * `mileage` rides along here (not via PATCH /state): the backend only knows per-plate
 * state once jobs are posted, so this submit is what persists `state.mileage` for resume.
 */
export interface FormStateSubmission {
  licensePlate?: string;
  mileage?: number;
  state: FormState;
}

/**
 * Inverse of DynamicFormComponent.getSubmissionPayload(): reconstructs a FormState
 * (raw form value, keyed by field.id) from a submission payload (per-option booleans
 * for choice/select fields). Use this when the backend returns the last-known answer
 * set in the same shape it was submitted, and you need to hydrate the form with it.
 */
export function buildStateFromSubmission(fields: FormFieldValue[], config: FormConfig): FormState {
  const byId: Map<string, unknown> = new Map(
    fields.map((entry: FormFieldValue): [string, unknown] => [entry.id, entry.value]),
  );
  const state: FormState = {};

  for (const field of config.fields) {
    if (field.type === 'description') continue;

    if ((field.type === 'choice' || field.type === 'select') && field.options?.length) {
      const anyKnown: boolean = field.options.some((option: FieldOption): boolean =>
        byId.has(String(option.value)),
      );
      if (!anyKnown) continue;
      const selected: FieldOption | undefined = field.options.find(
        (option: FieldOption): boolean => byId.get(String(option.value)) === true,
      );
      state[field.id] = selected ? selected.value : null;
      continue;
    }

    if (byId.has(field.id)) {
      state[field.id] = byId.get(field.id);
    }
  }

  return state;
}

@Injectable({ providedIn: 'root' })
export class FormStateService {
  private readonly httpService: HttpService = inject(HttpService);

  private readonly cache: WritableSignal<Map<string, FormState>> = signal<Map<string, FormState>>(
    new Map(),
  );

  public submit(payload: FormStateSubmission): Observable<unknown> {
    return this.httpService.post(API_ROUTES.PLANNER.jobsSubmit(), payload);
  }

  public setCachedState(licensePlate: string, state: FormState): void {
    this.cache.update(
      (current: Map<string, FormState>): Map<string, FormState> =>
        new Map(current).set(licensePlate, state),
    );
  }

  public getCachedState(licensePlate: string): FormState | undefined {
    return this.cache().get(licensePlate);
  }

  public hasCachedState(licensePlate: string): boolean {
    return this.cache().has(licensePlate);
  }

  public clearCachedState(licensePlate: string): void {
    this.cache.update((current: Map<string, FormState>): Map<string, FormState> => {
      const next: Map<string, FormState> = new Map(current);
      next.delete(licensePlate);
      return next;
    });
  }

  public clearAll(): void {
    this.cache.set(new Map());
  }
}
